import React, { useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import callPostRequest from "../api/callPostRequest";
import AddAccountForm from "./AddAccountForm";

// Styles
const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            tableContainer: {
                marginTop: theme.spacing(2),
                borderRadius: theme.shape.borderRadius * 2,
                overflow: 'hidden',
            },
            tableHeaderCell: {
                backgroundColor: theme.palette.grey[300],
                fontWeight: 'bold',
            },
        }),
);

// Data types
interface IsinDataApiAccount {
    id?: number;
    first_name: string;
    last_name: string;
    firm: string;
    email: string;
    status: string;
    api_key?: string;
    create_date?: string;
    update_date?: string;
}

interface ApiResponse {
    data: IsinDataApiAccount[];
    total: number;
}

export default function ApiAdmin() {
    const classes = useStyles();
    const [data, setData] = useState<IsinDataApiAccount[]>([]);
    const statusOptions = ['active', 'inactive'];
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newAccount, setNewAccount] = useState<IsinDataApiAccount>({
        first_name: '',
        last_name: '',
        firm: '',
        email: '',
        status: statusOptions[0],
        api_key: '',
    });

    // For delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

    const fetchData = () => {
        setLoading(true);
        fetch('/anna/api/v1/api-accounts')
                .then(res => {
                    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                    return res.json();
                })
                .then((json: ApiResponse) => {
                    setData(json.data);
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message);
                    setLoading(false);
                });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

    useEffect(() => {
        fetch('/anna/api/v1/api-accounts/isadmin')
                .then(res => {
                    if (!res.ok) throw new Error('Failed to fetch admin status');
                    return res.json();
                })
                .then((data: boolean) => {
                    setIsAdmin(data);
                })
                .catch(err => {
                    console.error(err);
                    setIsAdmin(false);
                });
    }, []);

    const handleInputChange = (field: keyof IsinDataApiAccount) => (
            event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewAccount({ ...newAccount, [field]: event.target.value });
    };

    const handleStatusChange = (email: string, newStatus: string) => {
        callPostRequest('/anna/api/v1/api-accounts/update-status', { email, status: newStatus })
                .then(() => fetchData())
                .catch(err => setError(err.message));
    };

    // Open confirmation dialog
    const confirmDelete = (email: string) => {
        setSelectedEmail(email);
        setDeleteDialogOpen(true);
    };

    // Perform actual delete after confirmation
    const handleDelete = () => {
        if (!selectedEmail) return;
        callPostRequest('/anna/api/v1/api-accounts/delete', { email: selectedEmail })
                .then(() => {
                    fetchData();
                    setDeleteDialogOpen(false);
                    setSelectedEmail(null);
                })
                .catch(err => setError(err.message));
    };

    if (isAdmin === null) {
        return (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography>Checking permissions...</Typography>
                </div>
        );
    }

    if (!isAdmin) {
        return (
                <div style={{ padding: 20 }}>
                    <Typography color="error">You are not permitted to see this page.</Typography>
                </div>
        );
    }

    const handleAdd = () => {
        // Remove api_key from the request data
        const { api_key, ...accountData } = newAccount;

        callPostRequest('/anna/api/v1/api-accounts', accountData)
                .then(() => {
                    fetchData();
                    setNewAccount({
                        first_name: '',
                        last_name: '',
                        firm: '',
                        email: '',
                        status: statusOptions[0],
                    });
                })
                .catch(err => setError(err.message));
    };

    return (
            <>
                <Title title="API Admin" />

                <TableContainer component={Paper} className={classes.tableContainer}>
                    {loading ? (
                            <div style={{ padding: 20, textAlign: 'center' }}>
                                <CircularProgress />
                                <Typography>Loading...</Typography>
                            </div>
                    ) : error ? (
                            <Typography color="error" style={{ padding: 20 }}>
                                Error: {error}
                            </Typography>
                    ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHeaderCell}>First Name</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Last Name</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Firm</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Email</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>API Key</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Created</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Updated</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map(row => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.first_name}</TableCell>
                                                <TableCell>{row.last_name}</TableCell>
                                                <TableCell>{row.firm}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                            select
                                                            value={row.status}
                                                            onChange={e => handleStatusChange(row.email, e.target.value)}
                                                            SelectProps={{ native: true }}
                                                    >
                                                        {statusOptions.map(option => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>{row.api_key}</TableCell>
                                                <TableCell>{new Date(row.create_date || '').toLocaleDateString()}</TableCell>
                                                <TableCell>{new Date(row.update_date || '').toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Button color="secondary" onClick={() => confirmDelete(row.email)}>
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                    )}
                </TableContainer>

                {/* Delete confirmation dialog */}
                <Dialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this account?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button color="secondary" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add form moved here */}
                <AddAccountForm
                        newAccount={newAccount}
                        statusOptions={statusOptions}
                        onChange={handleInputChange}
                        onAdd={handleAdd}
                />
            </>
    );
}
